import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Newspaper, ExternalLink, Loader2, Heart, Sparkles, X, Bookmark, BookmarkCheck, Mail, MessageCircle, Copy, Share2, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const GRADIENTS = [
  "from-amber-400/30 to-rose-400/20", "from-emerald-400/30 to-teal-400/20",
  "from-sky-400/30 to-indigo-400/20", "from-fuchsia-400/30 to-purple-400/20",
  "from-orange-400/30 to-yellow-400/20",
];
export const savedItems = () => { try { const v = JSON.parse(localStorage.getItem("anxya_saved_news") || "[]"); return Array.isArray(v) ? v.filter((x) => x && typeof x === "object" && x.id) : []; } catch { return []; } };
export const savedIds = () => savedItems().map((x) => x.id);

export default function News() {
  const { user, setUser } = useApp();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [upliftBusy, setUpliftBusy] = useState(false);
  const [subs, setSubs] = useState([]);
  const [forYou, setForYou] = useState(false);
  const upliftOn = !!user?.daily_uplift;

  useEffect(() => {
    if (!user) { setSubs([]); setForYou(false); return; }
    api.get("/news/preferences").then(({ data }) => {
      setSubs(data.subscriptions || []);
      if ((data.subscriptions || []).length || (data.top_topics || []).length) setForYou(true);
    }).catch(() => {});
  }, [user?.user_id]);

  const toggleSub = async (topic) => {
    if (!user) { toast.error("Sign in to follow topics"); return; }
    const next = subs.includes(topic) ? subs.filter((t) => t !== topic) : [...subs, topic];
    setSubs(next);
    try { await api.post("/news/subscriptions", { topics: next }); }
    catch { toast.error("Couldn't save. Try again."); }
  };

  const toggleUplift = async () => {
    if (!user) { toast.error("Sign in to turn on Daily Uplift"); return; }
    setUpliftBusy(true);
    try {
      const { data } = await api.post("/me/daily-uplift", { enabled: !upliftOn });
      if (setUser) setUser((u) => u ? { ...u, daily_uplift: data.daily_uplift, ...(data.credits_remaining != null ? { credits: data.credits_remaining } : {}) } : u);
      toast.success(data.daily_uplift ? (data.charged ? `Daily Uplift on 🌅 (${data.charged} credits)` : "Daily Uplift on — your morning good news is set 🌅") : "Daily Uplift turned off");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update. Try again."); }
    finally { setUpliftBusy(false); }
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/news?category=${encodeURIComponent(cat)}&personalized=${forYou}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], categories: ["All"] }))
      .finally(() => setLoading(false));
  }, [cat, forYou]);

  const openStory = (n) => {
    setActive(n);
    if (user && n?.category) api.post("/news/track", { category: n.category }).catch(() => {});
  };

  const items = data?.items || [];
  const cats = data?.categories || ["All"];

  return (
    <div className="max-w-6xl mx-auto px-5 pt-24 pb-16" data-testid="news-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2"><Newspaper className="text-brand" /><h1 className="font-serif text-4xl tracking-tight">Good News</h1></div>
        <Link to="/saved" data-testid="rays-of-hope-link" className="inline-flex items-center gap-1.5 text-sm rud-card rounded-full px-4 py-2 hover:border-brand/60 transition-colors"><BookmarkCheck size={15} className="text-brand" /> Rays of Hope</Link>
      </div>
      <p className="text-sec text-sm mt-2 max-w-2xl">
        Clean, kind, unbiased and non-violent — only stories that uplift. Read them right here, and share the good with
        the ones you love. A reminder that we are <span className="text-[var(--rud-text)]">one global family</span>. 🌍
      </p>

      <div className="mt-5 rounded-2xl border border-brand/25 bg-brand/5 p-4 flex items-center gap-4 flex-wrap" data-testid="daily-uplift-card">
        <div className="flex items-start gap-3 flex-1 min-w-[240px]">
          <Sparkles size={20} className="text-brand shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium">Daily Uplift email 🌅</div>
            <p className="text-[12px] text-sec mt-0.5">Every morning we'll email you one hand-picked good-news story — plus any big sky event happening that day. A gentle, hopeful start to your day. <span className="text-brand">One-time credits to switch on (region-fair).</span></p>
          </div>
        </div>
        <button data-testid="daily-uplift-toggle" onClick={toggleUplift} disabled={upliftBusy}
          className={`rounded-full px-5 h-10 text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60 ${upliftOn ? "bg-brand text-black" : "btn-ghost"}`}>
          {upliftBusy ? <Loader2 size={15} className="animate-spin" /> : upliftOn ? <><BookmarkCheck size={15} /> On — every 7am</> : <><Mail size={15} /> Turn on Daily Uplift</>}
        </button>
      </div>

      {user && (
        <div className="mt-5 rounded-2xl border border-rud bg-white/5 p-4" data-testid="news-personalize">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-brand" />
              <span className="text-sm font-medium">My Good News</span>
              <span className="text-[11px] text-sec">Follow topics you love — we quietly learn what you read and float it to the top.</span>
            </div>
            <button data-testid="for-you-toggle" onClick={() => setForYou((v) => !v)}
              className={`rounded-full px-4 h-9 text-sm flex items-center gap-1.5 transition-colors ${forYou ? "bg-brand text-black font-medium" : "btn-ghost"}`}>
              <Heart size={14} /> {forYou ? "For You: On" : "For You"}
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3" data-testid="topic-follow-chips">
            {(cats.filter((c) => c !== "All")).map((c) => {
              const on = subs.includes(c);
              return (
                <button key={c} data-testid={`follow-topic-${c}`} onClick={() => toggleSub(c)}
                  className={`h-8 px-3 rounded-full text-xs flex items-center gap-1 transition-colors ${on ? "bg-brand/15 text-brand border border-brand/40" : "rud-card text-sec hover:text-[var(--rud-text)]"}`}>
                  {on ? <BookmarkCheck size={12} /> : <Bookmark size={12} />} {c}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-6" data-testid="news-categories">
        {cats.map((c) => (
          <button key={c} data-testid={`news-cat-${c}`} onClick={() => setCat(c)}
            className={`h-9 px-4 rounded-full text-sm transition-colors ${cat === c ? "bg-brand text-black font-medium" : "rud-card text-sec hover:text-[var(--rud-text)]"}`}>
            {c}{data?.counts?.[c] ? ` · ${data.counts[c]}` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sec mt-10" data-testid="news-loading"><Loader2 className="animate-spin" size={18} /> Gathering good news…</div>
      ) : items.length === 0 ? (
        <p className="text-sec mt-10" data-testid="news-empty">No stories right now — please check back soon. 💛</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="news-list">
          {items.map((n, i) => (
            <motion.button
              key={n.id} onClick={() => openStory(n)} data-testid="news-card"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}
              className="rud-card rounded-2xl overflow-hidden group hover:-translate-y-1 hover:border-brand/50 transition-all flex flex-col text-left">
              {n.image ? (
                <div className="h-40 w-full overflow-hidden">
                  <img src={n.image} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                </div>
              ) : (
                <div className={`h-40 w-full bg-gradient-to-br ${GRADIENTS[i % GRADIENTS.length]} grid place-items-center`}><Heart className="text-brand/70" size={30} /></div>
              )}
              <div className="p-5 flex flex-col flex-1">
                <span className="text-[10px] uppercase tracking-widest text-brand flex items-center gap-1"><Sparkles size={11} /> {n.category}</span>
                <h3 className="font-serif text-lg mt-2 leading-snug">{n.title}</h3>
                {n.summary && <p className="text-sm text-sec mt-2 line-clamp-3 flex-1">{n.summary}</p>}
                <div className="flex items-center justify-between mt-3 text-xs text-sec">
                  <span>{n.source}</span>
                  <span className="inline-flex items-center gap-1 text-brand">Read here <ArrowIcon /></span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {active && <Reader item={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </div>
  );
}

const ArrowIcon = () => <ExternalLink size={11} />;

export function Reader({ item, onClose }) {
  const { user } = useApp();
  const [art, setArt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saved, setSaved] = useState(() => savedIds().includes(item.id));
  const [shareOpen, setShareOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const idle = useRef(null);
  const scrollRef = useRef(null);

  const resetIdle = () => {
    clearTimeout(idle.current);
    idle.current = setTimeout(() => { toast("Closed the reader after a quiet minute 🌙"); onClose(); }, 60000);
  };
  useEffect(() => {
    resetIdle();
    api.get(`/news/read?url=${encodeURIComponent(item.link)}`)
      .then(({ data }) => setArt(data))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(idle.current); window.removeEventListener("keydown", onKey); };
  }, []); // eslint-disable-line

  const shareText = `${item.title} — a little good news 🌸`;
  const shareUrl = item.link;
  const act = (fn) => { resetIdle(); fn(); };
  const toggleSave = () => act(() => {
    const list = savedItems();
    const next = saved ? list.filter((x) => x.id !== item.id)
      : [{ id: item.id, title: item.title, summary: item.summary || "", image: item.image || "", source: item.source, category: item.category, link: item.link, savedAt: Date.now() }, ...list];
    localStorage.setItem("anxya_saved_news", JSON.stringify(next));
    setSaved(!saved);
    toast.success(saved ? "Removed from Rays of Hope" : "Saved to Rays of Hope 🌟");
  });
  const email = () => act(() => { window.location.href = `mailto:?subject=${encodeURIComponent(item.title)}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`; });
  const whatsapp = () => act(() => { window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, "_blank"); });
  const copy = () => act(async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied — share it with your connects"); } catch { toast.error("Copy failed"); } });
  const toFeed = () => act(async () => {
    if (!user) { toast.error("Sign in to share to your feed"); return; }
    setPosting(true);
    try {
      await api.post("/showcase", { title: item.title, body: `${item.summary || shareText}\n\nRead more: ${shareUrl}`, category: "professional", media_url: item.image || "", media_type: "image" });
      toast.success("Shared to your feed — your connects will see it 💛");
      setShareOpen(false);
    } catch { toast.error("Couldn't share right now."); }
    finally { setPosting(false); }
  });

  const hero = (art?.image || item.image || "").trim();

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="news-reader">
      <motion.div initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
        className="rud-card rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-rud">
          <span className="text-[10px] uppercase tracking-widest text-brand flex items-center gap-1"><Sparkles size={11} /> {item.category} · {item.source}</span>
          <button data-testid="reader-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div ref={scrollRef} onScroll={resetIdle} className="overflow-y-auto px-6 py-5" data-testid="reader-content">
          <h2 className="font-serif text-2xl leading-snug">{item.title}</h2>
          {hero && <img src={hero} alt="" className="w-full rounded-xl mt-4 max-h-72 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
          {loading ? (
            <div className="flex items-center gap-2 text-sec mt-6"><Loader2 className="animate-spin" size={16} /> Opening the story…</div>
          ) : failed || !(art?.paragraphs?.length) ? (
            <div className="mt-4">
              <p className="text-sm text-sec">{item.summary}</p>
              <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand text-sm mt-3">Read the full story at {item.source} <ExternalLink size={13} /></a>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {art.paragraphs.map((p, i) => <p key={i} className="text-[15px] leading-relaxed text-[var(--rud-text)]">{p}</p>)}
              <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand text-xs mt-2">Source: {item.source} <ExternalLink size={12} /></a>
            </div>
          )}
        </div>

        <div className="border-t border-rud px-5 py-3 flex items-center justify-between gap-2">
          <span className="text-[10px] text-sec flex items-center gap-1"><Clock size={11} /> Closes after a quiet minute</span>
          <div className="flex items-center gap-2">
            <button data-testid="reader-save" onClick={toggleSave} className={`rounded-full h-9 px-3 text-sm flex items-center gap-1.5 border ${saved ? "bg-brand text-black border-brand" : "border-white/20 text-sec"}`}>
              {saved ? <BookmarkCheck size={15} /> : <Bookmark size={15} />} {saved ? "Saved" : "Save"}
            </button>
            <div className="relative">
              <button data-testid="reader-share" onClick={() => act(() => setShareOpen((v) => !v))} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><Share2 size={15} /> Share</button>
              {shareOpen && (
                <div className="absolute right-0 bottom-11 rud-card rounded-xl p-1.5 w-52 shadow-xl" data-testid="share-menu">
                  <ShareItem testid="share-feed" icon={<Sparkles size={15} />} label="Share to my feed & tag connects" onClick={toFeed} busy={posting} />
                  <ShareItem testid="share-whatsapp" icon={<MessageCircle size={15} />} label="WhatsApp" onClick={whatsapp} />
                  <ShareItem testid="share-email" icon={<Mail size={15} />} label="Email" onClick={email} />
                  <ShareItem testid="share-copy" icon={<Copy size={15} />} label="Copy link" onClick={copy} />
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

const ShareItem = ({ icon, label, onClick, testid, busy }) => (
  <button data-testid={testid} onClick={onClick} disabled={busy} className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded-lg hover:bg-white/10 text-left disabled:opacity-60">
    {busy ? <Loader2 size={15} className="animate-spin" /> : icon} {label}
  </button>
);
